
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/Pagination';
import { agentsAPI } from '../lib/api';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { id } from 'date-fns/locale';
import {
    Server,
    Wifi,
    WifiOff,
    Activity,
    Laptop,
    Trash2,
    Search,
    Edit2,
    Save,
    X
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

// WITA Timezone
const WITA_TIMEZONE = 'Asia/Makassar';

export function AgentHealthPage() {
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingAgent, setEditingAgent] = useState(null);
    const [editAlias, setEditAlias] = useState('');

    const loadData = React.useCallback(async () => {
        try {
            const response = await agentsAPI.getAll({
                page: currentPage,
                per_page: 5,
                q: searchQuery
            });

            // Handle response structure
            if (response.data.agents) {
                setAgents(response.data.agents);
                setTotalPages(response.data.total_pages);
                if (response.data.stats) {
                    setStats(response.data.stats);
                }
            } else {
                // Fallback for non-paginated API (just in case)
                setAgents(response.data);
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
            toast.error('Gagal memuat data server');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery]);

    useEffect(() => {
        // Debounce search
        const delayDebounceFn = setTimeout(() => {
            loadData();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        // Auto refresh interval (only if no search query to avoid jitter)
        if (!searchQuery) {
            const interval = setInterval(loadData, 30000);
            return () => clearInterval(interval);
        }
    }, [searchQuery, loadData]);



    const handleDelete = async (agentId, agentName) => {
        if (!window.confirm(`Apakah Anda yakin ingin menghapus agent "${agentName}"?\nSemua sertifikat terkait juga akan dihapus.`)) {
            return;
        }
        try {
            const response = await agentsAPI.delete(agentId);
            if (response.data.success) {
                toast.success('Agent berhasil dihapus');
                loadData();
            } else {
                toast.error(response.data.message || 'Gagal menghapus agent');
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat menghapus agent');
        }
    };

    const handleEditAlias = (agent) => {
        setEditingAgent(agent);
        setEditAlias(agent.alias || '');
    };

    const handleSaveAlias = async () => {
        if (!editingAgent) return;

        try {
            const response = await agentsAPI.updateAlias(editingAgent.id, editAlias);
            if (response.data.success) {
                toast.success(
                    <div className="flex flex-col gap-1">
                        <div className="font-semibold">✅ Alias Berhasil Diperbarui!</div>
                        <div className="text-sm">
                            Server: <span className="font-medium">{editingAgent.server_source}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Alias baru: {editAlias || '(tidak ada)'}
                        </div>
                    </div>,
                    { duration: 4000 }
                );
                setEditingAgent(null);
                setEditAlias('');
                loadData();
            } else {
                toast.error(response.data.message || 'Gagal memperbarui alias');
            }
        } catch (error) {
            toast.error(
                <div className="flex flex-col gap-1">
                    <div className="font-semibold">❌ Gagal Memperbarui Alias</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {error.response?.data?.message || error.message || 'Terjadi kesalahan'}
                    </div>
                </div>,
                { duration: 5000 }
            );
        }
    };

    const handleCancelEdit = () => {
        setEditingAgent(null);
        setEditAlias('');
    };

    const formatLastSeen = (timestamp) => {
        if (!timestamp) return 'Belum pernah';
        try {
            const date = new Date(timestamp);
            const witaDate = toZonedTime(date, WITA_TIMEZONE);
            return format(witaDate, 'dd MMM yyyy, HH:mm', { locale: id }) + ' WITA';
        } catch (e) {
            return timestamp;
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    if (loading) {
        return <div className="p-8 text-center">Memuat data server...</div>;
    }

    return (
        <motion.div
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div variants={itemVariants}>
                <h2 className="text-3xl font-bold tracking-tight">Status Server Agent</h2>
                <p className="text-muted-foreground mt-1">
                    Monitor kesehatan dan status koneksi server agent
                </p>
            </motion.div>

            {/* Stats Cards */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                <motion.div variants={itemVariants}>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Server</CardTitle>
                            <Server className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.total}</div>
                            <p className="text-xs text-muted-foreground">Terdaftar di sistem</p>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div variants={itemVariants}>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Online</CardTitle>
                            <Wifi className="h-4 w-4 text-green-600" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-green-600">{stats.online}</div>
                            <p className="text-xs text-muted-foreground">Aktif dalam 24 jam terakhir</p>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div variants={itemVariants}>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Offline</CardTitle>
                            <WifiOff className="h-4 w-4 text-red-600" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-red-600">{stats.offline}</div>
                            <p className="text-xs text-muted-foreground">Tidak merespon &gt; 24 jam</p>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Agents List */}
            <motion.div variants={itemVariants}>
                <Card>
                    <CardHeader>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <CardTitle>Daftar Server Agent</CardTitle>
                                <CardDescription>
                                    Daftar semua server yang menjalankan script monitoring SSL
                                </CardDescription>
                            </div>
                            <div className="relative w-full md:w-64">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Cari server..."
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 pl-8"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {agents.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    {searchQuery ? 'Tidak ada agent yang cocok dengan pencarian.' : 'Belum ada server agent yang terdaftar.'}
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {agents.map((agent) => (
                                        <div
                                            key={agent.id}
                                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`p-2 rounded-full mt-1 ${agent.status === 'online' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                                    } `}>
                                                    <Laptop className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-semibold">
                                                            {agent.alias || agent.server_source}
                                                        </h4>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6 text-muted-foreground hover:text-primary"
                                                            onClick={() => handleEditAlias(agent)}
                                                            title="Edit Alias"
                                                        >
                                                            <Edit2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                    {agent.alias && (
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            ID Server: {agent.server_source}
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                                                        {agent.agent_address}
                                                    </p>
                                                    <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Activity className="h-3 w-3" />
                                                            Memantau {agent.certificates_count} sertifikat
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <div className="text-right hidden sm:block">
                                                    <Badge variant={agent.status === 'online' ? 'default' : 'destructive'}
                                                        className={agent.status === 'online' ? 'bg-green-600 hover:bg-green-700' : ''}
                                                    >
                                                        {agent.status === 'online' ? 'ONLINE' : 'OFFLINE'}
                                                    </Badge>
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        Last seen: {formatLastSeen(agent.last_seen)}
                                                    </p>
                                                </div>

                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => handleDelete(agent.id, agent.server_source)}
                                                    title="Hapus Agent"
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="mt-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>

            {/* Edit Alias Dialog */}
            {editingAgent && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-background rounded-lg shadow-lg max-w-md w-full p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Edit Alias Server</h3>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleCancelEdit}
                                className="h-8 w-8"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    ID Server (Original)
                                </label>
                                <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                                    {editingAgent.server_source}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium" htmlFor="alias-input">
                                    Alias (Nama Kustom)
                                </label>
                                <input
                                    id="alias-input"
                                    type="text"
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 mt-1"
                                    placeholder="Masukkan nama kustom (opsional)"
                                    value={editAlias}
                                    onChange={(e) => setEditAlias(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveAlias();
                                        if (e.key === 'Escape') handleCancelEdit();
                                    }}
                                    autoFocus
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Kosongkan untuk menggunakan ID Server sebagai nama
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleCancelEdit}
                                className="flex-1"
                            >
                                Batal
                            </Button>
                            <Button
                                onClick={handleSaveAlias}
                                className="flex-1"
                            >
                                <Save className="h-4 w-4 mr-2" />
                                Simpan
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </motion.div>
    );
}

